import React, { useContext, useState } from "react";
import styled from "styled-components";
import { useRouter } from "next/router";
import { colors, media } from "@literati/public-ui-shared-react";
import { CheckoutContext } from "contexts/CheckoutProvider";
import {
  MembershipState,
  MembershipStep,
} from "components/Membership/membershipTypes";
import StepSelectPlan from "components/Step/StepSelectPlan";
import MembershipFaqSelectPlan from "components/Membership/MembershipFaqSelectPlan";
import { GuidedSignUpFaqClassName } from "components/GuidedSignUp/GuidedSignUpFaq";
import {
  FALL25_COUPON_CODE,
  isBudgetRangeGreaterThan,
} from "components/Step/stepUtil";
import { FEATURE_FLAG_SHOW_FALL25_COUPON } from "components/Membership/membershipFeatureFlags";
import { isSubscriptionPlanSlug } from "components/PlanSelection/planSelectionTypes";
import { getSelectedPlan } from "components/PlanSelection/planSelectionUtil";

const FaqContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  flex: 1;
  margin: 2rem 0 0;
  padding: 0 0 1rem;
  width: 100vw;
  background: ${colors.papierBlanc};

  ${media.sm} {
    margin: 3rem 0 0;
    align-items: center;
    padding: 0 0 1rem;
  }
`;

const StyledMembershipFaqSelectPlan = styled(MembershipFaqSelectPlan)`
  padding: 2rem 2rem 0;

  .${GuidedSignUpFaqClassName.COLLAPSIBLE_CONTAINER}:last-of-type {
    border-bottom: none;
  }

  ${media.sm} {
    padding: 3rem 2rem 0;
    max-width: 557px;
  }
`;

export const MembershipStepSelectPlan = ({
  routePrevStep,
  routeNextStep,
  onSubmit,
  setState,
  ...stepProps
}: MembershipState) => {
  const router = useRouter();
  const {
    addPromoCode,
    getCheckoutSession,
    getOrderSummary,
    updateSelectedPlan,
  } = useContext(CheckoutContext);

  const [formPlanSlug, setFormPlanSlug] = useState("");
  const monthlyPlanSlug = stepProps.plans?.monthly2025?.slug ?? "";

  const backButtonProps = {
    onClick: () => routePrevStep?.(),
  };

  // High-budget Fall25 path: custom footer “Next” replaces the default Formik submit so we can
  // sequence URL + addPromoCode + plan update before routing (Formik would only run onSubmit).
  const shouldApplyFall25Coupon =
    FEATURE_FLAG_SHOW_FALL25_COUPON &&
    isBudgetRangeGreaterThan(stepProps.budgetValues, 15) &&
    Boolean(monthlyPlanSlug && formPlanSlug === monthlyPlanSlug);

  const fall25NextButtonProps = shouldApplyFall25Coupon
    ? {
        onClick: async () => {
          // Keep banner/checkout in sync with other claim entry points (dialog, fall25-promo step).
          await router.push(
            {
              pathname: router.pathname,
              query: {
                ...router.query,
                extCouponCode: FALL25_COUPON_CODE,
              },
            },
            undefined,
            { shallow: true }
          );
          // Required: shallow URL alone does not attach the discount to the subscription checkout session.
          await addPromoCode?.(FALL25_COUPON_CODE);
          await getOrderSummary?.();
          await getCheckoutSession?.();
          // Skips Formik submit — persist plan like `useSelectPlan` would.
          if (isSubscriptionPlanSlug(monthlyPlanSlug)) {
            await updateSelectedPlan?.({ plan: monthlyPlanSlug });
            await getOrderSummary?.();
            await getCheckoutSession?.();
          }
          const selectedPlan = getSelectedPlan({
            plans: stepProps.plans,
            plansPrepaid: stepProps.plansPrepaid,
            selectedPlanSlug: monthlyPlanSlug,
          });
          if (selectedPlan) {
            setState?.((prev) => ({
              ...prev,
              selectedPlan,
              // Lets StepSelectPlan prefer persisted slug on back-nav instead of defaulting to offer radio.
              selectPlanChoiceCommitted: true,
            }));
          }
          await getCheckoutSession?.();
          // Always pass the coupon on navigation: `router.query` can still be stale here
          // after shallow push + awaits, so membership `onSubmit` → `routeNextStep()` would drop it.
          await routeNextStep?.({
            queries: {
              extCouponCode: FALL25_COUPON_CODE,
            },
          });
        },
      }
    : null;

  return (
    <StepSelectPlan
      {...stepProps}
      onSubmit={onSubmit}
      onSelectedPlanSlugChange={setFormPlanSlug}
      appendedContent={
        <FaqContainer>
          <StyledMembershipFaqSelectPlan />
        </FaqContainer>
      }
      stepFooterProps={{
        backButtonProps,
        // When not on the Fall25 offer path, omit nextButtonProps so StepLayout/Formik use the default submit button.
        ...(fall25NextButtonProps
          ? { nextButtonProps: fall25NextButtonProps }
          : {}),
      }}
      stepHeaderProps={{
        backButtonProps,
      }}
    />
  );
};

export const MEMBERSHIP_STEP_SELECT_PLAN: MembershipStep = {
  component: MembershipStepSelectPlan,
  id: "membership-id-select-plan",
  slug: "select-plan",
};

export default MembershipStepSelectPlan;
